import {
    Injectable, inject
} from "@angular/core";
import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";
import {
    ArrayUtil, StringUtil, ISearchSort, SearchSortDirection
} from "@shopliftr/common-js/data";
import {
    Chain,
    ChainSearchFilterOptions
} from "@shopliftr/common-js/shared";
import { IAutocompleteController } from "../../interfaces/autocomplete-controller";
import { IAutocompleteResults } from "../../interfaces/autocomplete-results";
import { IAutocompleteOption } from "../../interfaces/autocomplete-option";
import { IChainSearchOptions } from "../../interfaces/chain-search-options";
import { ChainApiClientService } from "../../iapi-application/services/api-client/chain/chain-api-client.service";

/**
 * Autocomplete controller for Chain entities
 * Provides autocomplete functionality with country and status filtering
 */
@Injectable({
    providedIn: "root"
})
export class ChainAutocompleteControllerService implements IAutocompleteController<Chain, IChainSearchOptions> {

    private readonly _chainClient = inject(ChainApiClientService);

    /**
     * IAutocompleteController function: autocomplete observable for the GenericAutocompleteMatControlComponent
     *
     * @param query - User's search input
     * @param options - Autocomplete configuration including country, inactive status and sorting
     * @returns Observable of filtered chain autocomplete results
     */
    autocomplete$(query?: string, options?: IChainSearchOptions): Observable<IAutocompleteResults<Chain>> {
        // Build sort parameter
        const sort: ISearchSort | undefined = options?.sort ? {
            field: options.sort,
            direction: (options.sortDir ?? SearchSortDirection.Ascending) as SearchSortDirection
        } : undefined;

        // Determine if we should get only active chains
        const activeOnly = options?.inactive === false;

        const filter = new ChainSearchFilterOptions();
        if (activeOnly) {
            filter.active = true;
        }

        return this._chainClient.getAll$(filter, sort ? [sort] : undefined).pipe(
            map((chains) => {
                let filteredChains = chains;

                // Apply query filter (client-side)
                if (!StringUtil.emptyString(query)) {
                    const queryLower = StringUtil.sanitizeString(query).toLowerCase();
                    filteredChains = chains.filter((chain) =>
                        chain.name?.toLowerCase().includes(queryLower));
                }

                // Apply country filter (client-side)
                if (options?.country) {
                    filteredChains = filteredChains.filter((chain) =>
                        chain.country === options.country);
                }

                // Convert chains to autocomplete options
                const autocompleteOptions: Array<IAutocompleteOption<Chain>> = filteredChains.map((chain) => ({
                    option: this.getAutocompleteDisplayName(chain, options),
                    value: chain
                }));

                return {
                    options: autocompleteOptions
                };
            })
        );
    }

    /**
     * IAutocompleteController Function that returns the string to be shown in the Autocomplete list and textbox when selected
     * @param value - The chain to get display name for
     * @param options - Options including country filter
     * @returns The display string for the chain
     */
    getAutocompleteDisplayName(value?: Chain, options?: IChainSearchOptions): string {
        // Check if country is specified in the options, if it return the name
        if (options?.country || !value?.country) {
            return value?.name;
        }
        // add the country as a suffix if the country is not specified in the options
        else if (value?.country) {
            return value?.name + " (" + value.country + ")";
        }
    }

    /**
     * IAutocompleteController function: Used as the Identity Track Expression for the MatOption List
     * @param index - Index in for loop
     * @param result - Item at index
     * @returns Unique identifier
     */
    trackByIdentifier(index: number, result: IAutocompleteOption<Chain>): any {
        return result?.value?.id;
    }

    /**
     * IAutocompletecontroller function to determine if the item is unique compared to the items in the array
     * @param value - The chain to check
     * @param items - Array of Chains
     * @returns True if the value is not in the items array, false otherwise
     */
    isUnique(value: Chain, items: Array<Chain>): boolean {
        return !ArrayUtil.contains(value, items);
    }
}
